/**
 * Autopilot "This Week" — the ranked cross-account ops queue (bold bet #2).
 *
 * `assembleWeeklyOps()` pre-assembles the week's operational work across ALL
 * accounts into ONE ranked, reasoned queue the operator reviews. It invents
 * nothing: every item is drawn from an existing PURE signal the app already
 * computes —
 *
 *   - at-risk accounts .......... `rankCustomersByRisk` (health.ts)
 *   - overdue follow-ups ........ `followUpDate` on live people (reminders.ts
 *                                 semantics, with an injectable clock)
 *   - overdue deliverables ...... `isDeliverableOverdue` (deliverablesSummary)
 *   - high scan findings ........ `ScanFinding[]` from `scanSystem` (caller
 *                                 passes them in; the engine stays pure)
 *   - weekly updates due ........ `assembleWeeklyUpdate` — an account with
 *                                 live work on the boards has something to
 *                                 report; a dormant account does not.
 *
 * Ranking is transparent by design: every item's `priorityScore` is a fixed
 * per-kind base (`WEEKLY_OPS_KIND_BASE`) plus a bounded, explainable
 * magnitude (days overdue, health deficit, blocker count) — so the operator
 * can always answer "why is this above that?". PURE by contract: no store,
 * no network, no clock reads unless `now` is omitted, no input mutation.
 * Empty input yields an empty queue — the engine never fabricates work to
 * make a week look busy.
 */

import type { Account, Deliverable, Person, ProjectTask, Ticket } from '../db/types';
import type { ScanFinding } from '../agent/systemScanner';
import { rankCustomersByRisk } from './health';
import { isDeliverableOverdue } from './deliverablesSummary';
import { assembleWeeklyUpdate } from './weeklyUpdate';

export type WeeklyOpsKind =
  | 'weekly_update'
  | 'at_risk'
  | 'overdue_followup'
  | 'overdue_deliverable'
  | 'stale_finding';

/** One entry in the week's ranked ops queue. */
export interface WeeklyOpsItem {
  /** Stable id: `weekly-ops:<kind>:<entityId>`. */
  id: string;
  /** The Account this item ties to, when the signal carries one. */
  accountId?: string;
  kind: WeeklyOpsKind;
  /** One-line headline, e.g. "Slipping Corp — account at risk". */
  title: string;
  /** The honest "why this is in your week" line, drawn from record data. */
  reason: string;
  /** Kind base + bounded magnitude — higher = do it sooner. */
  priorityScore: number;
}

export interface WeeklyOpsInput {
  people?: Person[];
  accounts?: Account[];
  tasks?: ProjectTask[];
  tickets?: Ticket[];
  deliverables?: Deliverable[];
  /** Pre-computed scan findings (from `scanSystem`); only `high` are queued. */
  findings?: ScanFinding[];
  /** Injectable clock for deterministic tests; defaults to now. */
  now?: number;
}

/**
 * The fixed per-kind base of `priorityScore` — exported so the ranking is
 * inspectable, testable, and explainable in the UI. Magnitude bonuses are
 * bounded per kind (see `MAX_MAGNITUDE`) so no single signal can run away.
 */
export const WEEKLY_OPS_KIND_BASE: Record<WeeklyOpsKind, number> = {
  at_risk: 60,
  stale_finding: 55,
  overdue_deliverable: 50,
  overdue_followup: 40,
  weekly_update: 20,
};

/** Per-kind cap on the magnitude bonus added to the base. */
const MAX_MAGNITUDE: Record<WeeklyOpsKind, number> = {
  at_risk: 40, // health deficit (100 − score), capped
  stale_finding: 25, // scanner sortKey, capped
  overdue_deliverable: 30, // days overdue, capped
  overdue_followup: 30, // days overdue, capped
  weekly_update: 20, // 5 per blocker, capped
};

const DAY_MS = 86_400_000;

function score(kind: WeeklyOpsKind, magnitude: number): number {
  const bounded = Math.max(0, Math.min(MAX_MAGNITUDE[kind], Math.round(magnitude)));
  return WEEKLY_OPS_KIND_BASE[kind] + bounded;
}

/** Whole days a date (YYYY-MM-DD or ISO) is past `now`; ≤ 0 = not yet. */
function daysOverdue(iso: string, now: number): number {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.floor((now - t) / DAY_MS);
}

/** "3 days overdue" / "1 day overdue". */
function overdueLabel(days: number): string {
  return `${days} day${days === 1 ? '' : 's'} overdue`;
}

// ---------------------------------------------------------------------------
// Per-signal collectors — each PURE, each producing zero items from nothing.
// ---------------------------------------------------------------------------

/** At-risk live customers, reasoned from their transparent health factors. */
function collectAtRisk(people: Person[], tasks: ProjectTask[], now: number): WeeklyOpsItem[] {
  return rankCustomersByRisk(people, { tasks, now })
    .filter((r) => r.health.band === 'at-risk')
    .map(({ person, health }) => {
      const topFactors = [...health.factors]
        .filter((f) => f.points < 0)
        .sort((a, b) => a.points - b.points)
        .slice(0, 3)
        .map((f) => f.label);
      return {
        id: `weekly-ops:at_risk:${person.id}`,
        kind: 'at_risk' as const,
        title: `${person.name} — account at risk`,
        reason: `Health ${health.score}/100${topFactors.length ? ` — ${topFactors.join('; ')}` : ''}`,
        priorityScore: score('at_risk', 100 - health.score),
      };
    });
}

/** Overdue follow-ups on live relationships (dead deals excluded). */
function collectOverdueFollowUps(people: Person[], now: number): WeeklyOpsItem[] {
  const out: WeeklyOpsItem[] = [];
  for (const person of people) {
    if (!person.followUpDate) continue;
    if (person.stage === 'closed-lost' || person.status === 'churned') continue;
    // Grant the due day itself — overdue only once the day has fully passed.
    const t = Date.parse(person.followUpDate);
    if (Number.isNaN(t) || t + DAY_MS > now) continue;
    const days = daysOverdue(person.followUpDate, now);
    out.push({
      id: `weekly-ops:overdue_followup:${person.id}`,
      kind: 'overdue_followup',
      title: `Follow up with ${person.name}`,
      reason: `Follow-up was due ${person.followUpDate} — ${overdueLabel(days)}${
        person.nextStep ? ` · next step: ${person.nextStep}` : ''
      }`,
      priorityScore: score('overdue_followup', days),
    });
  }
  return out;
}

/** Deliverables past their committed date and still owed. */
function collectOverdueDeliverables(deliverables: Deliverable[], now: number): WeeklyOpsItem[] {
  return deliverables
    .filter((d) => isDeliverableOverdue(d, now))
    .map((d) => {
      const days = daysOverdue(d.dueDate!, now);
      return {
        id: `weekly-ops:overdue_deliverable:${d.id}`,
        accountId: d.accountId,
        kind: 'overdue_deliverable' as const,
        title: `${d.accountName} · ${d.name}`,
        reason: `Deliverable due ${d.dueDate} — ${overdueLabel(days)} · status: ${d.status}`,
        priorityScore: score('overdue_deliverable', days),
      };
    });
}

/** High-severity scanner findings — the fires the sweep already found. */
function collectHighFindings(findings: ScanFinding[]): WeeklyOpsItem[] {
  return findings
    .filter((f) => f.severity === 'high')
    .map((f) => ({
      id: `weekly-ops:stale_finding:${f.id}`,
      kind: 'stale_finding' as const,
      title: f.title,
      reason: `${f.category} — ${f.detail}`,
      priorityScore: score('stale_finding', f.sortKey),
    }));
}

/**
 * Accounts due a weekly update: any account whose assembled digest has
 * something to report (shipped / in flight / next / blocked). A dormant
 * account with empty boards has nothing to say — no item, never filler.
 */
function collectWeeklyUpdatesDue(
  accounts: Account[],
  tasks: ProjectTask[],
  tickets: Ticket[],
  now: number,
): WeeklyOpsItem[] {
  const out: WeeklyOpsItem[] = [];
  for (const account of accounts) {
    const d = assembleWeeklyUpdate(account, { tasks, tickets, nowMs: now });
    const parts: string[] = [];
    if (d.delivered.length) parts.push(`${d.delivered.length} shipped`);
    if (d.inProgress.length) parts.push(`${d.inProgress.length} in flight`);
    if (d.next.length) parts.push(`${d.next.length} up next`);
    if (d.blockers.length) parts.push(`${d.blockers.length} blocked`);
    if (parts.length === 0) continue; // nothing to report — no update owed
    out.push({
      id: `weekly-ops:weekly_update:${account.id}`,
      accountId: account.id,
      kind: 'weekly_update',
      title: `${account.name} — weekly update due`,
      reason: `${parts.join(' · ')} — draft this week's update`,
      priorityScore: score('weekly_update', d.blockers.length * 5),
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

/**
 * Assemble the week's ranked ops queue from the live snapshots. PURE:
 * reads only its arguments; returns a new array sorted highest-priority
 * first (ties break alphabetically by title, then id, for determinism).
 */
export function assembleWeeklyOps(input: WeeklyOpsInput): WeeklyOpsItem[] {
  const now = input.now ?? Date.now();
  const people = input.people ?? [];
  const accounts = input.accounts ?? [];
  const tasks = input.tasks ?? [];
  const tickets = input.tickets ?? [];

  const items: WeeklyOpsItem[] = [
    ...collectAtRisk(people, tasks, now),
    ...collectOverdueFollowUps(people, now),
    ...collectOverdueDeliverables(input.deliverables ?? [], now),
    ...collectHighFindings(input.findings ?? []),
    ...collectWeeklyUpdatesDue(accounts, tasks, tickets, now),
  ];

  return items.sort(
    (a, b) =>
      b.priorityScore - a.priorityScore ||
      a.title.localeCompare(b.title) ||
      a.id.localeCompare(b.id),
  );
}

/** Counts per kind — powers the panel's group badges. */
export function weeklyOpsCounts(items: WeeklyOpsItem[]): Record<WeeklyOpsKind, number> {
  const counts: Record<WeeklyOpsKind, number> = {
    weekly_update: 0,
    at_risk: 0,
    overdue_followup: 0,
    overdue_deliverable: 0,
    stale_finding: 0,
  };
  for (const item of items) counts[item.kind]++;
  return counts;
}
